/**
 * ProcessHistoryTab — pełna historia procesów akceptacji rekordu (F17 T17.1.8).
 *
 * Miejsca na karcie rekordu: kartoteka własna — na stałe pod formularzem, sekcja „Akceptacje”
 * (`RecordFormPage`); 7 klas dokumentów i kontrahent — element układu formularza „Proces
 * akceptacji” (`process_view`: `document-additional-renderers.tsx`, `ContractorDetail.tsx`),
 * widoczny tylko, gdy układ karty go zawiera.
 * W przeciwieństwie do `ProcessApprovalBanner` (bieżąca/najnowsza instancja + akcje)
 * pokazuje WSZYSTKIE instancje rekordu — także zakończone/anulowane —
 * każdą jako sekcję z `ProcessStepsTable`. Opcjonalnie (prop `actions`, element „Proces akceptacji”
 * z włączonym „Pokaż przyciski decyzji”) karty niosą też akcje banera: panel decyzji na karcie
 * żywej sprawy i „Anuluj proces” na karcie sprawy właściwej — wg wspólnej reguły slotu.
 *
 * Dane: `GET /processes/instances?moduleCode=&recordId=` (lista, klucz wspólny z
 * bannerem) + `GET /processes/instances/:id` per instancja (useQueries; te same
 * klucze co banner → react-query deduplikuje). N+1 akceptowalne na karcie detalu.
 *
 * Szczegóły sprawy niedostępne dla pytającego (404 `INSTANCE_NOT_FOUND`, patrz
 * `isInstanceDetailGone`) to nie awaria: sekcja takiej sprawy zostaje z nagłówkiem z listy, ale
 * bez komunikatu błędu, bez tabeli kroków ze starych danych z pamięci i bez „Pokaż proces" —
 * tak samo jak baner.
 */

import type { ReactElement } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import {
  instanceDetailKey,
  isInstanceDetailGone,
  recordInstancesKey,
  resolveRecordProcessSlots,
  retryInstanceDetailQuery,
  selectRecordInstanceRows,
} from '@/hooks/useActiveInstanceForRecord';
import type { DecisionGate } from '@/hooks/useSaveBeforeDecision';
import { useUserLabels } from '@/hooks/useUserLabels';
import {
  fetchInstanceDetail,
  fetchInstancesForRecord,
  type InstanceDetail,
} from '@/lib/processes/instance-api';
import { finalNodeLabelOrNull } from '@/lib/processes/step-tone';

import { ApprovalActionsPanel } from './ApprovalActionsPanel';
import { CancelInstanceButton } from './CancelInstanceButton';
import { ProcessGraphViewer } from './ProcessGraphViewer';
import { ProcessStepsTable } from './ProcessStepsTable';

/** Włącza przyciski akcji na kartach historii (element „Proces akceptacji” z opcją „Pokaż przyciski decyzji”). */
export interface ProcessHistoryActions {
  /** Bramka przed otwarciem okna decyzji (formularz w edycji: najpierw zapis) — ta sama co w banerze. */
  beforeDecision?: DecisionGate;
  /**
   * Czy karta żywej sprawy pokazuje panel decyzji („Akceptacja” i drogi). Domyślnie `true`; przy
   * `false` panelu nie ma, a „Anuluj proces” / „Unieważnij” zostaje wg zwykłej reguły slotu.
   * Używa tego strona rekordu modułu własnego w trybie edycji (patrz `RecordFormPage`).
   */
  decisions?: boolean;
}

export interface ProcessHistoryTabProps {
  moduleCode: string;
  recordId: string;
  testIdPrefix?: string;
  /**
   * Czy pokazywać sprawy anulowane (status `cancelled`). Domyślnie `true` — dotychczasowe
   * zachowanie (np. sekcja „Akceptacje” w `RecordFormPage`); element układu „Proces akceptacji”
   * przekazuje tu swoje ustawienie (domyślnie wyłączone). Ukryte sprawy nie dostają też
   * zapytania o szczegóły; gdy po odfiltrowaniu nic nie zostaje — ten sam stan pusty co dla
   * rekordu bez spraw.
   */
  showCancelled?: boolean;
  /**
   * Przyciski akcji na kartach — brak / `null` = sama historia (dotychczasowe zachowanie). Gdy podane,
   * karta ŻYWEJ sprawy dostaje panel decyzji („Akceptacja” i drogi; sam się ukrywa, gdy użytkownik
   * nie może teraz decydować), a karta sprawy „właściwej” (żywa, inaczej najnowsza nieanulowana)
   * — „Anuluj proces” / „Unieważnij” (ukrywa się bez prawa anulowania). Tak jak w banerze nad
   * zakładkami (wspólna reguła slotu), bez „Wyślij do akceptacji”. Sprawę żywą i właściwą wyznacza
   * PEŁNA lista spraw — ukrycie anulowanych (`showCancelled`) tego nie zmienia.
   */
  actions?: ProcessHistoryActions | null;
}

export function ProcessHistoryTab({
  moduleCode,
  recordId,
  testIdPrefix = 'process-history',
  showCancelled = true,
  actions = null,
}: ProcessHistoryTabProps): ReactElement {
  const { t } = useTranslation();
  const userLabels = useUserLabels();
  const [openFlowInstanceId, setOpenFlowInstanceId] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: recordInstancesKey(moduleCode, recordId),
    queryFn: () => fetchInstancesForRecord(moduleCode, recordId),
    enabled: !!moduleCode && !!recordId,
    select: selectRecordInstanceRows,
  });

  // Najnowsze instancje na górze (po numerze); anulowane odfiltrowane PRZED sortowaniem
  // i zapytaniami o szczegóły, gdy `showCancelled` jest wyłączone.
  const allInstances = listQuery.data ?? [];
  const instances = allInstances
    .filter((inst) => showCancelled || inst.status !== 'cancelled')
    .sort((a, b) => b.instanceNumber - a.instanceNumber);
  // Sprawy były, ale wszystkie ukryte filtrem anulowanych — inny komunikat niż „brak spraw”.
  const onlyHiddenCancelled = instances.length === 0 && allInstances.length > 0;
  // Slot rekordu z PEŁNEJ listy (nie z odfiltrowanej): wskazuje karty, na których leżą akcje.
  const slots = resolveRecordProcessSlots(allInstances);
  // Żywa sprawa nigdy nie jest anulowana, więc zawsze jest wśród wyświetlanych — jej zapytanie o
  // szczegóły sprawdzamy niżej (parytet z banerem).
  const liveIdx = slots.live ? instances.findIndex((inst) => inst.id === slots.live?.id) : -1;

  const detailQueries = useQueries({
    queries: instances.map((inst) => ({
      queryKey: instanceDetailKey(inst.id),
      queryFn: () => fetchInstanceDetail(inst.id),
      // Ta sama reguła ponowień co baner, hak i okno z listy (bez ponowień stanów końcowych).
      retry: retryInstanceDetailQuery,
    })),
  });

  // Parytet z banerem: gdy szczegóły ŻYWEJ sprawy nie wczytały się z awarii (nie 404 „niedostępna”,
  // patrz `isInstanceDetailGone`), baner pokazuje sam błąd i żadnych akcji — nie znamy stanu sprawy.
  // Tu tak samo: ani panelu decyzji, ani „Anuluj proces” na żadnej karcie.
  const liveQuery = liveIdx >= 0 ? detailQueries[liveIdx] : undefined;
  const liveDetailFailed =
    liveQuery !== undefined && liveQuery.isError && !isInstanceDetailGone(liveQuery);
  const actionsEnabled = actions !== null && !liveDetailFailed;

  // Szczegóły do pokazania: `null`, gdy jeszcze niewczytane ALBO niedostępne (404
  // `INSTANCE_NOT_FOUND`) — wtedy bez starych danych z pamięci (zapytanie zachowuje poprzednie
  // `data` po nieudanym odświeżeniu).
  const detailOf = (query: UseQueryResult<InstanceDetail> | undefined): InstanceDetail | null =>
    query === undefined || isInstanceDetailGone(query) ? null : (query.data ?? null);

  const statusLabel = (status: string): string =>
    t(`tenant.processes.banner.status.${status}`, status);
  const dateOf = (iso: string | null): string => (iso ? iso.slice(0, 10) : '');

  // Modal "Pokaż proces" — jeden wspólny Modal dla wszystkich instancji (portal, więc pozycja
  // w drzewie nie ma znaczenia); sterowany id otwartej instancji, detal bierzemy z już
  // pobranych `detailQueries` (bez nowego zapytania sieciowego).
  const openFlowIdx = instances.findIndex((i) => i.id === openFlowInstanceId);
  const openFlowInstance = openFlowIdx >= 0 ? instances[openFlowIdx] : null;
  const openFlowDetail = openFlowIdx >= 0 ? detailOf(detailQueries[openFlowIdx]) : null;
  const hasFlowDiagram = (openFlowDetail?.snapshot.nodes.length ?? 0) > 0;

  if (listQuery.isPending) {
    return (
      <p data-testid={`${testIdPrefix}-loading`} className="text-sm text-text-secondary">
        {t('tenant.processes.history.loading', 'Ładowanie historii…')}
      </p>
    );
  }

  if (listQuery.isError) {
    return (
      <p role="alert" data-testid={`${testIdPrefix}-error`} className="text-sm text-error">
        {t('tenant.processes.history.error', 'Nie udało się wczytać historii akceptacji.')}
      </p>
    );
  }

  if (instances.length === 0) {
    return (
      <div
        data-testid={`${testIdPrefix}-empty`}
        data-reason={onlyHiddenCancelled ? 'only-cancelled' : undefined}
        className="space-y-3"
      >
        <p className="text-sm text-text-secondary">
          {onlyHiddenCancelled
            ? t(
                'tenant.processes.history.emptyOnlyCancelled',
                'Brak aktywnych ani zakończonych spraw — anulowane sprawy są ukryte.',
              )
            : t(
                'tenant.processes.history.empty',
                'Proces nie został jeszcze rozpoczęty dla tego rekordu.',
              )}
        </p>
        <ProcessStepsTable steps={[]} testIdPrefix={`${testIdPrefix}-empty-steps`} />
      </div>
    );
  }

  return (
    <div data-testid={testIdPrefix} className="space-y-4">
      {instances.map((inst, idx) => {
        const detailQuery = detailQueries[idx];
        const detail = detailOf(detailQuery);
        const detailGone = detailQuery !== undefined && isInstanceDetailGone(detailQuery);
        const isCancelled = inst.status === 'cancelled';
        const mutedSecondaryClass = isCancelled ? 'text-text-muted' : 'text-text-secondary';
        const showsDecision =
          actionsEnabled && actions?.decisions !== false && slots.live?.id === inst.id;
        const showsCancel = actionsEnabled && !slots.free && slots.relevant?.id === inst.id;
        return (
          <section
            key={inst.id}
            data-testid={`${testIdPrefix}-instance`}
            data-cancelled={isCancelled ? 'true' : 'false'}
            className={`rounded border p-3 ${isCancelled ? 'border-border-secondary' : 'border-border-primary'}`}
          >
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <h4
                className={`text-sm font-medium ${isCancelled ? 'text-text-secondary' : 'text-text-primary'}`}
              >
                {inst.processName ?? t('tenant.processes.history.unnamed', 'Proces')} #
                {inst.instanceNumber}
              </h4>
              <span
                data-status={inst.status}
                className={`rounded bg-bg-tertiary px-2 py-0.5 text-xs ${mutedSecondaryClass}`}
              >
                {finalNodeLabelOrNull(inst.finalNodeLabel) ?? statusLabel(inst.status)}
              </span>
              <span className={`text-xs ${mutedSecondaryClass}`}>{dateOf(inst.submittedAt)}</span>
              {detail && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setOpenFlowInstanceId(inst.id)}
                  data-testid={`${testIdPrefix}-flow-button-${inst.id}`}
                >
                  {t('tenant.processes.flow.button', 'Pokaż proces')}
                </Button>
              )}
              {showsDecision || showsCancel ? (
                // Akcje karty po prawej; na wąskim ekranie zawijane w kolejną linię.
                <div
                  data-testid={`${testIdPrefix}-actions-${inst.id}`}
                  className="ml-auto flex flex-wrap items-center gap-2"
                >
                  {showsDecision ? (
                    <ApprovalActionsPanel
                      moduleCode={moduleCode}
                      recordId={recordId}
                      beforeDecision={actions?.beforeDecision}
                      testIdPrefix={`${testIdPrefix}-decision`}
                    />
                  ) : null}
                  {showsCancel ? (
                    <CancelInstanceButton
                      moduleCode={moduleCode}
                      recordId={recordId}
                      instanceId={inst.id}
                      instanceStatus={inst.status}
                      testIdPrefix={`${testIdPrefix}-cancel`}
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className={isCancelled ? 'opacity-60' : undefined}>
              {detail ? (
                <ProcessStepsTable
                  steps={detail.steps}
                  initiator={{
                    userId: detail.instance.submittedByUserId,
                    comment: detail.instance.initiatorComment,
                    submittedAt: detail.instance.submittedAt,
                  }}
                  userLabels={userLabels}
                  snapshot={detail.snapshot}
                  testIdPrefix={`${testIdPrefix}-steps-${inst.id}`}
                />
              ) : detailGone ? null : detailQuery?.isError ? (
                <p role="alert" className="text-sm text-error">
                  {t(
                    'tenant.processes.history.detailError',
                    'Nie udało się wczytać szczegółów tej instancji.',
                  )}
                </p>
              ) : (
                <p className="text-sm text-text-secondary">
                  {t('tenant.processes.history.loadingDetail', 'Ładowanie…')}
                </p>
              )}
            </div>
          </section>
        );
      })}
      <Modal
        open={openFlowInstanceId !== null}
        onClose={() => setOpenFlowInstanceId(null)}
        size="2xl"
        title={t('tenant.processes.flow.modalTitle', {
          processName: openFlowInstance?.processName ?? t('tenant.processes.history.unnamed'),
          number: openFlowInstance?.instanceNumber ?? '',
          defaultValue: 'Przebieg procesu: {{processName}} #{{number}}',
        })}
      >
        <div className="h-[70vh] w-full" data-testid={`${testIdPrefix}-flow-modal-body`}>
          {openFlowDetail && hasFlowDiagram ? (
            <ProcessGraphViewer
              snapshot={openFlowDetail.snapshot}
              instance={openFlowDetail.instance}
              definitionChanged={openFlowDetail.definitionChanged}
            />
          ) : (
            <p className="text-sm text-text-secondary">
              {t('tenant.processes.flow.empty', 'Brak danych diagramu dla tej instancji.')}
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
}
